import React, { createContext, useContext } from "react";
import { Box, Group, Loader, Text } from "@mantine/core";
import { IconChevronRight } from "@tabler/icons-react";
import styles from "./sales.module.css";

/**
 * CSS-grid table shared by both Sales screens. Each screen passes its own exact
 * column template and min-width floor (Transactions 900, Payments/Purchases
 * 820). The container scrolls horizontally below the floor rather than letting
 * columns collapse (layout floor rule 1). Header is NOT sticky (it cannot be
 * inside an overflow-x:auto container — see the design doc).
 */

const TemplateContext = createContext<string>("");

export interface SalesTableHeader {
  label: string;
  align?: "right";
}

interface SalesTableProps {
  columnTemplate: string;
  minWidth: number;
  headers: SalesTableHeader[];
  /**
   * True whenever a fetch is in flight. Drives two affordances: a centered
   * loader when there are no rows yet, and a dimmed body during a refetch when
   * rows are already shown.
   */
  loading?: boolean;
  /** Row count after filtering — drives the empty state. */
  isEmpty: boolean;
  emptyText: string;
  children: React.ReactNode;
}

export function SalesTable({
  columnTemplate,
  minWidth,
  headers,
  loading,
  isEmpty,
  emptyText,
  children,
}: SalesTableProps) {
  return (
    <Box className={styles.tableContainer}>
      <Box className={styles.tableScroll}>
        <Box style={{ minWidth }}>
          <Box
            className={styles.tableHeader}
            style={{ gridTemplateColumns: columnTemplate }}
          >
            {headers.map((h, i) => (
              <Box
                key={`${h.label}-${i}`}
                className={h.align === "right" ? styles.cellRight : undefined}
              >
                {h.label}
              </Box>
            ))}
          </Box>
          <TemplateContext.Provider value={columnTemplate}>
            {loading && isEmpty ? (
              <Group justify="center" gap={10} className={styles.loadingState}>
                <Loader size="sm" />
                <Text className={styles.loadingText}>Loading…</Text>
              </Group>
            ) : isEmpty ? (
              <Text className={styles.emptyState}>{emptyText}</Text>
            ) : (
              <Box
                style={{
                  opacity: loading ? 0.55 : 1,
                  transition: "opacity .12s",
                }}
              >
                {children}
              </Box>
            )}
          </TemplateContext.Provider>
        </Box>
      </Box>
    </Box>
  );
}

interface SalesTableRowProps {
  active?: boolean;
  onClick: () => void;
  /** One node per column, in order. A trailing chevron cell is appended. */
  children: React.ReactNode;
}

/** A body row. Reads the column template from its parent SalesTable. */
export function SalesTableRow({
  active,
  onClick,
  children,
}: SalesTableRowProps) {
  const columnTemplate = useContext(TemplateContext);
  return (
    <Box
      className={`${styles.tableRow} ${active ? styles.tableRowActive : ""}`}
      style={{ gridTemplateColumns: columnTemplate }}
      onClick={onClick}
      role="button"
    >
      {children}
      <Box className={styles.chevron}>
        <IconChevronRight size={15} stroke={2.2} />
      </Box>
    </Box>
  );
}
